/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { FunctionComponent } from 'preact';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useCallback, useContext, useState } from 'preact/hooks';
import { Context, TrainContextType } from './TrainContext';
import { MessageSeverity } from './TrainIcons.types';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeProps } from '../utils/UNSAFE_mergeProps';

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';

import { Connector } from './Connector';
import { StepIcon } from './StepIcon';
import { StepLabel } from './StepLabel';
import { TrainRedwoodTheme } from './themes/redwood/TrainTheme';
import { TrainVariantOptions, TrainStyles } from './themes/TrainStyles.css';

interface StepProps {
  /**
   * Unique step id
   */
  id: string;

  /**
   * Determines if the step is disabled or not
   */
  isDisabled: boolean;

  /**
   * Determines if the step has been visited or not
   */
  isVisited: boolean;

  /**
   * Determines which icon the step has from the following types 'error', 'warning', 'confirmation', 'info' and 'none'.
   */
  messageType?: Exclude<MessageSeverity, 'none'>;

  /**
   * Step Label
   */
  label: string;
}

/**
 * Step component
 */

export const Step: FunctionComponent<StepProps> = ({
  id,
  isDisabled,
  isVisited,
  messageType,
  label
}) => {
  const { onSelect, /* selectedStep ,*/ numberOfSteps, selectedStepIndex, index } =
    useContext<TrainContextType>(Context) || // get the context object or...
    (() => {
      // if there's no context object, throw an error
      throw new Error('Step component must be rendered within a Train component.');
    })();

  const isCurrent = selectedStepIndex === index;

  const [isActive, setIsActive] = useState(false);

  const { interactionProps, applyPseudoHoverStyle, applyHoverStyle } = useInteractionStyle(
    isDisabled || isCurrent
  );

  const isConnectorConnected = selectedStepIndex > index;

  const onSelectHandler = useCallback(
    (event: Event) => {
      if (!isDisabled && !isCurrent) {
        onSelect(event, id);
      }
    },
    [isDisabled, onSelect, id]
  );

  const { pressProps } = usePress(onSelectHandler, {});

  const isLast = index !== numberOfSteps - 1;

  const onMouseUpHandler = useCallback(() => {
    setIsActive(false);
  }, [isActive]);

  const onMouseDownHandler = useCallback(() => {
    setIsActive(true);
  }, [isActive]);

  const { styles, variantClasses } = useComponentTheme<TrainVariantOptions, TrainStyles>(
    TrainRedwoodTheme,
    {
      stepDisabled: isDisabled ? 'isDisabled' : 'notDisabled',
      needsEventsHover: applyHoverStyle ? 'isNeedsEventsHover' : 'notNeedsEventsHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      stepActive: isActive && !isCurrent ? 'isActive' : 'notActive'
    }
  );

  const mergedProps = mergeProps(pressProps, interactionProps, {
    onMouseUp: onMouseUpHandler,
    onMouseDown: onMouseDownHandler
  });

  return (
    <span class={styles.stepContainerStyle}>
      {isLast && <Connector isConnected={isConnectorConnected} />}
      <span {...mergedProps} class={classNames([styles.stepBaseStyle, variantClasses])}>
        <StepIcon isVisited={isVisited} isDisabled={isDisabled} messageType={messageType} />
        <StepLabel
          label={label}
          isVisited={isVisited}
          isDisabled={isDisabled}
          messageType={messageType}
        />
      </span>
    </span>
  );
};
